import React, { useState, useEffect, useContext, useRef } from 'react';
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import axiosInstance from '../axiosConfig';
import { AuthContext } from '../AuthContext';
import DataTable from 'react-data-table-component';
import Swal from 'sweetalert2';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import * as XLSX from 'xlsx';
import Select from 'react-select';

export default function WorkerSalaryAdmin() {
  const { user } = useContext(AuthContext);
  // Salary screen is calculation/finalization only. Advance payment management stays in Worker Advance Management.
  const paymentControlsEnabled = false;
  const showSalaryStatus = true;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const toggleSidebar = () => { setIsSidebarVisible(!isSidebarVisible); };
  
  const fileInputRef = useRef(null);
  
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('All');

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/api/worker-salary/admin/summary?month=${selectedMonth}&year=${selectedYear}&auth=${user?.Auth || ''}&dept=${encodeURIComponent(user?.Department || '')}&name=${encodeURIComponent(user?.Name || '')}`);
      
      const initializedData = res.data.map(item => ({
        ...item,
        NetPayable: calculateNet(item.TotalEarned, item.AdvancesDeducted, item.Penalties || 0)
      }));
      
      setData(initializedData);
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to fetch summary', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, [selectedMonth, selectedYear]);

  const calculateNet = (earned, advances, penalties) => {
    const net = parseFloat(earned || 0) - parseFloat(advances || 0) - parseFloat(penalties || 0);
    return net > 0 ? net : 0;
  };

  const handleFinalize = async () => {
    try {
      // Filter out rows with 0 earned unless they have a PayoutId
      const validPayouts = data.filter(d => parseFloat(d.TotalEarned) > 0 || d.PayoutId);
      
      if (validPayouts.length === 0) {
        return Swal.fire('Info', 'No valid records to finalize.', 'info');
      }

      const payouts = validPayouts.map(d => ({
        EmployeeID: d.EmployeeID,
        WorkerName: d.WorkerName,
        TotalEarned: d.TotalEarned,
        PresentDays: d.PresentDays,
        AdvancesDeducted: d.AdvancesDeducted,
        Penalties: d.Penalties,
        NetPayable: d.NetPayable
      }));

      const payload = {
        month: selectedMonth,
        year: selectedYear,
        payouts
      };

      setLoading(true);
      await axiosInstance.post('/api/worker-salary/finalize', payload);
      fetchSummary();
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to finalize salaries', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async (row) => {
    if (!row.PayoutId) {
      return Swal.fire('Warning', 'Please finalize the salary first before marking as Paid.', 'warning');
    }

    try {
      const result = await Swal.fire({
        title: 'Mark as Paid?',
        text: `Are you sure you want to mark ${row.WorkerName}'s salary as paid?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#10b981',
        cancelButtonColor: '#ef4444',
        confirmButtonText: '<i class="bi bi-check-circle"></i> Yes, Mark Paid'
      });

      if (result.isConfirmed) {
        await axiosInstance.post('/api/worker-salary/mark-paid', { id: row.PayoutId });
        Swal.fire({
          icon: 'success',
          title: 'Paid!',
          text: 'Status updated successfully.',
          timer: 1500,
          showConfirmButton: false
        });
        fetchSummary();
      }
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to update status', 'error');
    }
  };

  const handleSendWhatsApp = async (row) => {
    if (!row.PayoutId) {
      return Swal.fire('Warning', 'Please finalize the salary first before sending WhatsApp.', 'warning');
    }

    try {
      const result = await Swal.fire({
        title: 'Send WhatsApp Slip?',
        text: `Are you sure you want to send the salary slip to ${row.WorkerName} via WhatsApp?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#25D366',
        cancelButtonColor: '#6c757d',
        confirmButtonText: '<i class="bi bi-whatsapp"></i> Yes, Send'
      });

      if (result.isConfirmed) {
        Swal.fire({
          title: 'Sending...',
          text: 'Please wait while the WhatsApp message is being sent.',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });
        
        await axiosInstance.post('/api/worker-salary/send-whatsapp', { 
          EmployeeID: row.EmployeeID,
          Month: selectedMonth,
          Year: selectedYear
        });
        
        Swal.fire({
          icon: 'success',
          title: 'Sent!',
          text: 'WhatsApp slip sent successfully.',
          timer: 1500,
          showConfirmButton: false
        });
      }
    } catch (err) {
      console.error(err);
      const errMsg = err.response?.data?.error || 'Failed to send WhatsApp slip';
      Swal.fire('Error', errMsg, 'error');
    }
  };
  const handleExportExcel = () => {
    if (data.length === 0) {
      return Swal.fire('Info', 'No data available to export.', 'info');
    }
    const exportData = data.map(d => ({
      'Emp ID': d.EmployeeID,
      'Name': d.WorkerName,
      'Dept': d.Department,
      'Present Days': d.PresentDays,
      'Total Earned': parseFloat(d.TotalEarned).toFixed(2),
      'Advances': parseFloat(d.AdvancesDeducted).toFixed(2),
      'Penalties': parseFloat(d.Penalties || 0).toFixed(2),
      'Net Payable': parseFloat(d.NetPayable).toFixed(2)
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Salary_Report');
    
    const monthName = new Date(0, selectedMonth - 1).toLocaleString('default', { month: 'short' });
    const fileName = `Worker_Salary_${monthName}_${selectedYear}.xlsx`;
    
    XLSX.writeFile(workbook, fileName);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Optional: Reset file input so same file can be uploaded again if needed
    if (fileInputRef.current) {
        fileInputRef.current.value = "";
    }

    try {
      const result = await Swal.fire({
        title: 'Bulk Pay via Excel',
        text: `Are you sure you want to bulk mark salaries as Paid using this Excel file for ${new Date(0, selectedMonth - 1).toLocaleString('default', { month: 'long' })} ${selectedYear}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#10b981',
        cancelButtonColor: '#ef4444',
        confirmButtonText: 'Yes, Upload & Mark Paid'
      });

      if (result.isConfirmed) {
        setLoading(true);
        const formData = new FormData();
        formData.append('excelSheet', file);
        formData.append('month', selectedMonth);
        formData.append('year', selectedYear);

        const res = await axiosInstance.post('/api/worker-salary/upload-payment-sheet', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: `${res.data.successCount} salaries marked as Paid. ${res.data.skippedCount || 0} rows skipped; ${res.data.whatsappSent || 0} WhatsApp notifications sent.`,
          confirmButtonColor: '#10b981'
        });
        
        fetchSummary();
      }
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to process Excel file', 'error');
    } finally {
      setLoading(false);
    }
  };

  const customTableStyles = {
    headRow: {
      style: {
        backgroundColor: '#f8fafc',
        color: '#475569',
        fontSize: '14px',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        borderBottom: '2px solid #e2e8f0',
      },
    },
    rows: {
      style: {
        fontSize: '14px',
        color: '#334155',
        backgroundColor: '#ffffff',
        '&:hover': {
          backgroundColor: '#f1f5f9',
          transition: 'all 0.2s',
        },
        borderBottom: '1px solid #f1f5f9',
      },
    },
  };

  const columns = [
    { name: 'Emp ID', selector: row => row.EmployeeID, sortable: true, width: '100px', cell: row => <span className="fw-bold text-secondary">{row.EmployeeID}</span> },
    { name: 'Name', selector: row => row.WorkerName, sortable: true, width: '180px', cell: row => <span className="fw-bold text-dark">{row.WorkerName}</span> },
    { name: 'Department', selector: row => row.Department, sortable: true, minWidth: '150px', wrap: true },
    { 
      name: 'Present Days', 
      selector: row => row.PresentDays, 
      sortable: true, 
      width: '130px',
      cell: row => (
        <span className="badge bg-primary bg-opacity-10 text-primary border border-primary-subtle rounded-pill px-3 py-2">
          <i className="bi bi-calendar-check me-1"></i> {row.PresentDays}
        </span>
      )
    },
    { name: 'Total Earned', selector: row => parseFloat(row.TotalEarned).toFixed(2), sortable: true, cell: row => <span className="fw-bold text-dark">₹{parseFloat(row.TotalEarned).toFixed(2)}</span> },
    { name: 'Advances', selector: row => parseFloat(row.AdvancesDeducted).toFixed(2), sortable: true, cell: row => <span className="text-danger fw-medium">-₹{parseFloat(row.AdvancesDeducted).toFixed(2)}</span> },
    { 
      name: 'Penalties (Rs)', 
      cell: row => (
        <div className="input-group input-group-sm" style={{ width: '100px' }}>
          <span className="input-group-text bg-light text-muted border-end-0">₹</span>
          <input 
            type="number" 
            className="form-control border-start-0 shadow-none fw-bold text-danger"
            value={row.Penalties || 0}
            disabled
            title="Penalty is calculated from approved raised tickets only"
            style={{ backgroundColor: '#f8fafc', cursor: 'not-allowed' }}
          />
        </div>
      ),
      width: '140px'
    },
    { 
      name: 'Net Payable', 
      selector: row => parseFloat(row.NetPayable).toFixed(2), 
      sortable: true,
      cell: row => <span className="fw-bold text-success fs-6">₹{parseFloat(row.NetPayable).toFixed(2)}</span>
    },
    { 
      name: 'Advance payment', 
      cell: row => {
        if (!row.PayoutId) {
          return (
            <span className="badge rounded-pill px-3 py-2 bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25">
              <i className="bi bi-info-circle me-1"></i> No advance balance
            </span>
          );
        } else if (row.PaymentStatus === 'Advance applied') {
          return (
            <span className="badge rounded-pill px-3 py-2 bg-success bg-opacity-10 text-success border border-success border-opacity-25">
              <i className="bi bi-check-circle-fill me-1"></i> Advance applied
            </span>
          );
        } else if (parseFloat(row.NetPayable) <= 0) {
          return (
            <span className="badge rounded-pill px-3 py-2 bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25">
              <i className="bi bi-slash-circle me-1"></i> No Payout
            </span>
          );
        } else {
          return (
            <span className="badge rounded-pill px-3 py-2 bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25">
              <i className="bi bi-clock-fill me-1"></i> Pending
            </span>
          );
        }
      },
      sortable: true,
      width: '130px'
    },
    {
      name: 'Action',
      width: '210px',
      cell: row => {
        const waButton = false ? (
          <button
            className="btn btn-sm ms-2"
            onClick={() => handleSendWhatsApp(row)}
            title={`Send WhatsApp Slip (${row.WhatsApp_Number})`}
            style={{
              backgroundColor: '#25D366',
              color: 'white',
              borderRadius: '6px',
              border: 'none',
              padding: '6px 10px',
              fontWeight: '600',
              transition: 'all 0.2s',
              boxShadow: '0 2px 4px rgba(37, 211, 102, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseOver={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
            onMouseOut={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            <i className="bi bi-whatsapp"></i>
          </button>
        ) : null;

        if (!row.PayoutId) {
          return <span className="text-muted small fst-italic"><i className="bi bi-dash-circle"></i> No advance payment</span>;
        } else if (row.PaymentStatus === 'Advance applied') {
          return <div className="d-flex align-items-center"><span className="text-muted small me-1"><i className="bi bi-check2-all text-success fs-5 align-middle"></i> Advance applied</span>{waButton}</div>;
        } else if (parseFloat(row.NetPayable) <= 0) {
          return <div className="d-flex align-items-center"><span className="text-muted small fst-italic me-1"><i className="bi bi-dash"></i> N/A</span>{waButton}</div>;
        } else if (paymentControlsEnabled) {
          return <button className="btn btn-sm btn-success" onClick={() => handleMarkPaid(row)}><i className="bi bi-cash-coin"></i> Mark Paid</button>;
        }
        return <span className="text-muted small">Awaiting payment update</span>;
      }
    }
  ];

  const uniqueDepartments = ['All', ...new Set(data.map(item => item.Department).filter(Boolean))].sort();

  const filteredData = data.filter(item => {
    const matchesSearch = item.WorkerName && item.WorkerName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDept = selectedDepartment === 'All' || item.Department === selectedDepartment;
    return matchesSearch && matchesDept;
  });

  return (
    <>
    <style>{`
      .premium-header-bg {
        background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
        border-radius: 16px;
        box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
      }
      .glass-card {
        background: rgba(255, 255, 255, 0.95);
        backdrop-filter: blur(10px);
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 16px;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05);
      }
      .custom-datepicker .react-datepicker-wrapper input {
        border-radius: 50rem;
        padding: 0.5rem 1rem;
        border: 1px solid #cbd5e1;
        font-weight: 600;
        color: #334155;
        box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.02);
      }
      .custom-datepicker .react-datepicker-wrapper input:focus {
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
        outline: none;
      }
    `}</style>
    
    <div className="d-flex dashboard">
      <div className={isSidebarVisible ? "sidebar-container" : "sidebar-hidden"}>
        <Sidebar />
      </div>
      <div className="flex-grow-1" style={{ backgroundColor: '#f4f7f6', minHeight: '100vh' }}>
        <Header toggleSidebar={toggleSidebar} isSidebarVisible={isSidebarVisible} />
        
        <main className="main-container p-4" style={{ height: "calc(100vh - 70px)", overflowY: 'auto' }}>
          <div className="container-fluid max-w-7xl mx-auto">
            
            {/* Premium Header Area */}
            <div className="premium-header-bg p-4 mb-4 d-flex flex-column flex-md-row justify-content-between align-items-center text-white">
              <div>
                <h2 className="mb-1 fw-bold" style={{ letterSpacing: '-0.5px' }}>
                  <i className="bi bi-wallet2 me-3 text-info"></i>Worker Payroll
                </h2>
                <p className="mb-0 text-slate-300 text-opacity-75" style={{ fontSize: '14px' }}>
                  Finalize and process monthly salary payouts for all workers.
                </p>
              </div>
              
              <div className="d-flex flex-column flex-md-row gap-3 align-items-md-center mt-3 mt-md-0">
                <div className="position-relative">
                  <i className="bi bi-search position-absolute text-secondary" style={{ top: '50%', left: '16px', transform: 'translateY(-50%)' }}></i>
                  <input
                    type="text"
                    className="form-control border-0 shadow-sm"
                    placeholder="Search by name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ 
                      minWidth: '220px', 
                      borderRadius: '50rem', 
                      paddingLeft: '40px',
                      paddingTop: '8px',
                      paddingBottom: '8px',
                      backgroundColor: '#ffffff',
                      color: '#1e293b',
                      fontWeight: '500',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div className="d-flex gap-3 align-items-center bg-white bg-opacity-10 p-2 rounded-pill shadow-sm">
                  <div className="d-flex align-items-center px-2 gap-2">
                    <i className="bi bi-calendar3 text-white me-1"></i>
                    <select 
                      className="form-select form-select-sm border-0 rounded-pill shadow-none" 
                      value={selectedMonth} 
                      onChange={e => setSelectedMonth(Number(e.target.value))}
                      style={{ minWidth: '100px', cursor: 'pointer' }}
                    >
                      {[...Array(12)].map((_, i) => (
                        <option key={i+1} value={i+1}>
                          {new Date(0, i).toLocaleString('default', { month: 'short' })}
                        </option>
                      ))}
                    </select>
                    <select 
                      className="form-select form-select-sm border-0 rounded-pill shadow-none" 
                      value={selectedYear} 
                      onChange={e => setSelectedYear(Number(e.target.value))}
                      style={{ minWidth: '80px', cursor: 'pointer' }}
                    >
                      {[...Array(5)].map((_, i) => {
                        const yr = new Date().getFullYear() - 2 + i;
                        return <option key={yr} value={yr}>{yr}</option>;
                      })}
                    </select>
                  </div>
                  {false && <button 
                    className="btn btn-info text-white shadow-lg rounded-pill px-4 fw-bold d-flex align-items-center gap-2" 
                    onClick={handleFinalize} 
                    disabled={loading}
                    style={{ transition: 'all 0.2s' }}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    {loading ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-check2-square"></i>}
                    Finalize All
                  </button>}
                </div>
              </div>
            </div>

            {/* Actions & Data Table Card */}
            <div className="d-flex justify-content-between align-items-center mb-3 gap-3">
              <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ maxWidth: '450px' }}>
                <div className="d-flex align-items-center bg-white rounded-pill px-3 py-2 shadow-sm border border-light flex-grow-1">
                  <i className="bi bi-funnel text-primary me-2"></i>
                  <span className="text-muted fw-bold small me-2">Department:</span>
                  <div style={{ flex: 1 }}>
                    <Select
                      value={{ value: selectedDepartment, label: selectedDepartment }}
                      onChange={(selectedOption) => setSelectedDepartment(selectedOption ? selectedOption.value : 'All')}
                      options={uniqueDepartments.map(dept => ({ value: dept, label: dept }))}
                      styles={{
                        control: (base) => ({
                          ...base,
                          border: 'none',
                          boxShadow: 'none',
                          backgroundColor: 'transparent',
                          minHeight: '30px',
                          cursor: 'pointer'
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: '0 8px',
                        }),
                        singleValue: (base) => ({
                          ...base,
                          fontWeight: 'bold',
                          color: '#1e293b'
                        }),
                        dropdownIndicator: (base) => ({
                          ...base,
                          padding: '4px'
                        }),
                        indicatorSeparator: () => ({ display: 'none' })
                      }}
                      isSearchable={true}
                    />
                  </div>
                </div>
                {selectedDepartment !== 'All' && (
                  <button 
                    className="btn btn-white shadow-sm border border-light rounded-pill px-3 text-danger fw-bold d-flex align-items-center gap-1"
                    onClick={() => setSelectedDepartment('All')}
                    title="Clear Filter"
                  >
                    <i className="bi bi-x-circle"></i> Clear
                  </button>
                )}
              </div>

              <div className="d-flex gap-2">
                {paymentControlsEnabled && <>
                  <button 
                    className="btn text-white fw-medium shadow-sm rounded-pill px-4 d-flex align-items-center gap-2" 
                    style={{ backgroundColor: '#10b981', transition: 'all 0.2s' }}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={loading}
                  >
                    <i className="bi bi-cloud-arrow-up"></i> Upload Excel (Bulk Pay)
                  </button>
                  <input type="file" accept=".xlsx, .xls" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileUpload} />
                </>}

                <button 
                  className="btn btn-success text-white shadow-sm rounded-pill px-4 fw-bold d-flex align-items-center gap-2" 
                  onClick={handleExportExcel} 
                  disabled={loading || data.length === 0}
                  style={{ transition: 'all 0.2s' }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  <i className="bi bi-file-earmark-excel"></i> Export Excel
                </button>
              </div>
            </div>
            
            {/* Data Table Card */}
            <div className="glass-card overflow-hidden">
              <div className="card-body p-0">
                <DataTable
                  columns={showSalaryStatus ? columns.filter(column => column.name !== 'Action') : columns.filter(column => !['Advance payment', 'Action'].includes(column.name))}
                  data={filteredData}
                  progressPending={loading}
                  progressComponent={
                    <div className="d-flex flex-column justify-content-center align-items-center py-5 my-5">
                      <div className="spinner-border text-info mb-3" role="status" style={{ width: '3rem', height: '3rem', borderWidth: '0.25em' }}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <h5 className="text-secondary fw-bold">Fetching Payroll Data...</h5>
                      <p className="text-muted small">Please wait while we crunch the numbers.</p>
                    </div>
                  }
                  pagination
                  highlightOnHover
                  responsive
                  customStyles={customTableStyles}
                  noDataComponent={
                    <div className="text-center p-5 text-muted">
                      <i className="bi bi-inbox fs-1 text-secondary mb-3 d-block"></i>
                      <h5>No payroll data found for the selected month.</h5>
                      <p>Try selecting a different month.</p>
                    </div>
                  }
                />
              </div>
            </div>
            
          </div>
        </main>
      </div>
    </div>
    </>
  );
}
